import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { BaseComponent } from '@shared/components/base/base.component';
import { ToastStackComponent } from '@shared/components/toast/stack/toast-stack.component';
import { ForbiddenService } from '@shared/services/local/forbidden.service';
import { LoadingService } from '@shared/services/local/loading.service';
import { NotFoundService } from '@shared/services/local/not-found.service';
import { BaseLayoutHeaderComponent } from './header/base-layout-header.component';
import { BaseLayoutSidebarComponent } from './sidebar/base-layout-sidebar.component';
import { LAYOUT_BREAKPOINT } from '@shared/tokens/layout-breakpoint.token';

@Component({
  selector: 'backoffice-base-layout',
  imports: [
    RouterModule,
    ToastStackComponent,
    BaseLayoutSidebarComponent,
    BaseLayoutHeaderComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.css',
})
export class BaseLayoutComponent extends BaseComponent {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------

  private readonly forbiddenService = inject(ForbiddenService);

  private readonly notFoundService = inject(NotFoundService);

  private readonly router = inject(Router);

  private readonly loadingService = inject(LoadingService);

  protected readonly breakpoint = inject(LAYOUT_BREAKPOINT).sidebarBreakpoint;

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly isForbidden = computed(() => this.forbiddenService.isForbidden());

  readonly notFound = computed(() => this.notFoundService.notFound());

  readonly isNavigating = computed(() => !!this.router.currentNavigation());

  readonly isLoading = computed(() => this.loadingService.isLoading());

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Routes flagged `data: { fullBleed: true }` own their surface — the shell drops its padding. */
  readonly fullBleed = signal(this.resolveFullBleed());

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor() {
    super();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fullBleed.set(this.resolveFullBleed()));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Whether the ACTIVE leaf route opted out of the shell padding.
   *
   * @returns {boolean} True when the leaf route data carries fullBleed.
   */
  private resolveFullBleed(): boolean {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.data['fullBleed'] === true;
  }
}
